import { memo } from 'react'
import { RadioGroup } from '@/components/ui/radio-group'
import StageHeader from './StageHeader'
import ReviewAudienceItem from './ReviewAudienceItem'
import { InfoIcon } from '@/components/icons'

const ReviewAudiencesList = memo(
    ({
        currency,
        visibleStages,
        availableStages,
        collapsedStages,
        selectedAudience,
        getSelectedAudiences,
        getCreativesForAudience,
        getAudienceDisplayName,
        getAudienceBudget,
        usesCampaignBudget = false,
        getCampaignObjective,
        getAudienceGenderTargets,
        getAudienceAgeTargets,
        getAudienceDeviceTargets,
        getAudienceLocationData,
        getAudiencePlatformPlacementTargets,
        getAudienceCustomEventType,
        toggleStageCollapse,
        toggleAllStages,
        areAllStagesCollapsed,
        handleAudienceSelection,
        handleAudienceNameChange,
    }) => {
        return (
            <div className="">
                <div className="tw:p-6">
                    <h3 className="tw:text-white tw:!text-lg tw:!font-semibold tw:mb-2">Review Campaign</h3>
                    <div className="tw:flex tw:items-center tw:gap-2">
                        <InfoIcon className="tw:text-[#899CBD] tw:w-4 tw:h-4" />
                        <span className="tw:text-white tw:!text-sm">
                            Click on radio button next to audience name to show details about that audience.
                        </span>
                    </div>
                </div>

                {/* Audience stages list */}
                <RadioGroup
                    value={selectedAudience ? `${selectedAudience.stage.id}_${selectedAudience.audience.id}` : ''}
                    onValueChange={(value) => {
                        if (value) {
                            const [stageId, audienceId] = value.split('_')
                            handleAudienceSelection(stageId, audienceId)
                        }
                    }}
                >
                    <div className="tw:flex tw:gap-6 tw:flex-col tw:w-full tw:max-w-full tw:overflow-hidden">
                        {visibleStages.map((stage, index) => {
                            const selectedAudiences = getSelectedAudiences(stage.id)
                            const isFirstStage = index === 0
                            const isCollapsed = collapsedStages.has(stage.id)

                            return (
                                <div
                                    key={stage.id}
                                    className="tw:flex tw:gap-4 tw:flex-col tw:w-full tw:max-w-full tw:overflow-hidden"
                                >
                                    {/* Stage Header */}
                                    <StageHeader
                                        stage={stage}
                                        collapsedStages={collapsedStages}
                                        toggleStageCollapse={toggleStageCollapse}
                                        audienceCount={selectedAudiences.length}
                                        availableStages={availableStages}
                                        isFirstStage={isFirstStage}
                                        toggleAllStages={toggleAllStages}
                                        areAllStagesCollapsed={areAllStagesCollapsed}
                                    />

                                    {/* Audiences */}
                                    {!isCollapsed && availableStages.includes(stage.id) && (
                                        <div className="tw:flex tw:flex-col tw:border-t tw:border-b tw:divide-y tw:divide-gray-600 tw:border-gray-600 tw:w-full tw:max-w-full tw:overflow-hidden">
                                            {selectedAudiences.map((audience) => {
                                                const creatives = getCreativesForAudience(audience.id, stage.id)
                                                const isSelected =
                                                    selectedAudience?.audience?.id === audience.id &&
                                                    selectedAudience?.stage?.id === stage.id
                                                const displayName = getAudienceDisplayName(
                                                    stage.id,
                                                    audience.id,
                                                    audience.name,
                                                )
                                                const budget = getAudienceBudget(stage.id, audience.id)
                                                const campaignObjective = getCampaignObjective()
                                                const genderTargets = getAudienceGenderTargets(stage.id, audience.id)
                                                const ageTargets = getAudienceAgeTargets(stage.id, audience.id)
                                                const deviceTargets = getAudienceDeviceTargets(stage.id, audience.id)
                                                const locationData = getAudienceLocationData(stage.id, audience.id)
                                                const placementTargets = getAudiencePlatformPlacementTargets(
                                                    stage.id,
                                                    audience.id,
                                                )
                                                const conversionType = getAudienceCustomEventType(stage.id, audience.id)

                                                return (
                                                    <ReviewAudienceItem
                                                        stage={stage}
                                                        budget={budget}
                                                        usesCampaignBudget={usesCampaignBudget}
                                                        audience={audience}
                                                        currency={currency}
                                                        creatives={creatives}
                                                        isSelected={isSelected}
                                                        displayName={displayName}
                                                        genderTargets={genderTargets}
                                                        ageTargets={ageTargets}
                                                        deviceTargets={deviceTargets}
                                                        locationData={locationData}
                                                        placementTargets={placementTargets}
                                                        conversionType={conversionType}
                                                        key={`${stage.id}_${audience.id}`}
                                                        campaignObjective={campaignObjective}
                                                        onNameChange={handleAudienceNameChange}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </RadioGroup>
            </div>
        )
    },
)

ReviewAudiencesList.displayName = 'ReviewAudiencesList'

export default ReviewAudiencesList
